import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Languages, Sparkles } from "lucide-react";

const TYPES = ["vocabulary", "reading", "writing", "grammar", "conversation"];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function TutorLanguage() {
  const loc = useLocation();
  const lang = loc.pathname.endsWith("/french") ? "french" : "english";
  const language = lang === "french" ? "French" : "English";
  const nav = useNavigate();
  const [form, setForm] = useState({ exercise_type: "vocabulary", level: "Beginner", focus: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/api/tutor/language", { language, ...form });
      const { id, ...rest } = data;
      setResult(rest);
      toast.success(`${language} exercise ready`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed.";
      toast.error(msg);
      if (e?.response?.status === 402) nav("/tutor/pricing");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`tutor-lang-${lang}`}>
      <div className="label-eyebrow flex items-center gap-2"><Languages className="w-3 h-3 text-[#D92D20]" /> Teacher4Me</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-1">
        {language === "French" ? "Centre de français" : "English language center"}
      </h1>
      <p className="text-sm text-[#4B5563] mt-1">
        Vocabulary, reading, writing, grammar and conversation prompts — all AI-generated.
      </p>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6 grid sm:grid-cols-3 gap-4">
        <div>
          <label className="text-sm font-semibold">Type</label>
          <select data-testid="lang-type" value={form.exercise_type}
            onChange={(e) => setForm({ ...form, exercise_type: e.target.value })}
            className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold">Level</label>
          <select data-testid="lang-level" value={form.level}
            onChange={(e) => setForm({ ...form, level: e.target.value })}
            className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
            {LEVELS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold">Focus (optional)</label>
          <input data-testid="lang-focus" value={form.focus}
            onChange={(e) => setForm({ ...form, focus: e.target.value })}
            placeholder="e.g. travel, food, jobs"
            className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="sm:col-span-3">
          <button data-testid="lang-submit" onClick={submit} disabled={busy}
            className="btn-primary disabled:opacity-50 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate exercise"}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid="lang-result">
          <h3 className="font-display font-bold text-xl">{result.title}</h3>
          <p className="text-sm text-[#4B5563] mt-1">{result.instructions}</p>
          {result.passage && <div className="mt-3 p-3 bg-[#F9FAFB] rounded-lg text-sm whitespace-pre-wrap">{result.passage}</div>}
          <ol className="mt-4 list-decimal pl-6 space-y-3 text-sm">
            {result.items?.map((it, i) => (
              <li key={i}>
                <div className="font-semibold">{it.prompt}</div>
                <div className="text-[#064E3B]">Answer: {it.answer}</div>
                {it.explanation && <div className="text-[#4B5563] text-xs mt-1">{it.explanation}</div>}
              </li>
            ))}
          </ol>
          {result.vocabulary?.length > 0 && (
            <div className="mt-4">
              <div className="font-display font-bold">Vocabulary</div>
              <ul className="mt-2 text-sm space-y-1">
                {result.vocabulary.map((v, i) => (
                  <li key={`${v.word}-${i}`}><b>{v.word}</b> — {v.definition} <span className="text-[#4B5563]">({v.example})</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
